/**
 * Hooks for curated lists (admin CRUD + visitor loading)
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

// ============================================
// Admin Hooks
// ============================================

export function useCuratedLists(orgId: string | undefined, mapId: string | undefined) {
  return useQuery({
    queryKey: ["curated-lists", orgId, mapId],
    queryFn: async () => {
      if (!orgId || !mapId) throw new Error("Missing orgId or mapId");
      const res = await api.maps.orgs[":orgId"].maps[":mapId"].lists.$get({
        param: { orgId, mapId },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to fetch curated lists");
      }
      const data = await res.json();
      return data.lists;
    },
    enabled: !!orgId && !!mapId,
  });
}

export function useCreateCuratedList(orgId: string | undefined, mapId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      if (!orgId || !mapId) throw new Error("Missing orgId or mapId");
      const res = await api.maps.orgs[":orgId"].maps[":mapId"].lists.$post({
        param: { orgId, mapId },
        json: data,
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string; slug?: string };
        throw new Error(err.error ?? "Failed to create list");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["curated-lists", orgId, mapId] });
    },
  });
}

export function useUpdateCuratedList(orgId: string | undefined, mapId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
      ...data
    }: {
      listId: string;
      name?: string;
      slug?: string;
      description?: string | null;
      isActive?: boolean;
      sortOrder?: number;
    }) => {
      if (!orgId || !mapId) throw new Error("Missing orgId or mapId");
      const res = await api.maps.orgs[":orgId"].maps[":mapId"].lists[":listId"].$patch({
        param: { orgId, mapId, listId },
        json: data,
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to update list");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["curated-lists", orgId, mapId] });
    },
  });
}

export function useUpdateCuratedListItems(orgId: string | undefined, mapId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
      items,
    }: {
      listId: string;
      items: Array<{ poiId: string; note?: string }>;
    }) => {
      if (!orgId || !mapId) throw new Error("Missing orgId or mapId");
      const res = await api.maps.orgs[":orgId"].maps[":mapId"].lists[":listId"].items.$put({
        param: { orgId, mapId, listId },
        json: { items },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to update items");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["curated-lists", orgId, mapId] });
    },
  });
}

export function useDeleteCuratedList(orgId: string | undefined, mapId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (listId: string) => {
      if (!orgId || !mapId) throw new Error("Missing orgId or mapId");
      const res = await api.maps.orgs[":orgId"].maps[":mapId"].lists[":listId"].$delete({
        param: { orgId, mapId, listId },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to delete list");
      }
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["curated-lists", orgId, mapId] });
    },
  });
}

// ============================================
// Visitor Hooks
// ============================================

export function useLoadCuratedList(
  orgSlug: string | undefined,
  mapSlug: string | undefined,
  listSlug: string | undefined
) {
  return useQuery({
    queryKey: ["curated-list-detail", orgSlug, mapSlug, listSlug],
    queryFn: async () => {
      if (!orgSlug || !mapSlug || !listSlug) throw new Error("Missing params");
      const res = await api.maps.lists[":orgSlug"][":mapSlug"][":listSlug"].$get({
        param: { orgSlug, mapSlug, listSlug },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? "List not found");
      }
      const data = await res.json();
      return data.list;
    },
    enabled: !!orgSlug && !!mapSlug && !!listSlug,
    staleTime: 5 * 60 * 1000,
  });
}
